

import React from "react";
import { i18nManager } from "../../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { ITournamentEdition, ITournamentEditionRegisteredParticipant, ITournamentEditionPrize, TournamentPrizeType, TournamentService, BaseService, ConsusService, SanitizedResponse } from "../../../../../../services";
import { AlertInterface, BoolHelper, ButtonManageRef, Classname, Dropdown, DropdownManageRef, FormControl, Scheme, StringHelper, TextInput } from "@rivtn/noseur";
import Snapper from "../../../../../shared/Snapper";
import { Schemes, Utils } from "../../../../../../utils";
import { ConfirmDialog, Siren } from "../../../../../shared/ConfirmDialog";
import { AddOrEditTournamentPrize } from "../../../create_tournament/sections/PrizePool";

interface PrizePoolDetailProps {
    refetch: () => void;
    distributed?: boolean;
    tournamentEdition: ITournamentEdition;
    tournamentPrize: ITournamentEditionPrize;
    setSearchParams: ReactRouterDom.SetURLSearchParams;
    dataComponentState?: [React.ReactElement | undefined, React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>];
}

function PrizePoolDetail(props: PrizePoolDetailProps) {

    const consusService = ConsusService.getInstance();
    const tournamentService = TournamentService.getInstance();
    const [dataComponent, setDataComponent] = props.dataComponentState ?? [];
    const labels = i18nManager.Labels.host.manage_tournament.result_prize_distro;
    const { tournamentEdition, tournamentPrize, distributed, setSearchParams } = props;
    const projectedWinnerName = Utils.getResourceName(tournamentPrize.projected_winner?.resource);
    const projectedWinnerImage = Utils.getResourceImage(tournamentPrize.projected_winner?.resource);
    const currentEditionDistributed = !distributed && tournamentEdition.meta.prize_pool_distributed;

    return (<div key={tournamentPrize.external_id} className="mt-participant-data">
        <div className="mt-participant-data-hctrl">
            <Snapper header={{
                ["TROPHY"]: labels.details.trophy_prize_media_snap,
                ["TOP_WINNER"]: labels.details.top_winner_prize_media_snap,
            }[tournamentPrize.type]} template={[{ p: "tour_name", v: tournamentEdition.name }, { p: "name", v: tournamentPrize.title }, { p: "position", v: Utils.numberWithNth(tournamentPrize.position) }]} />
            <i className="fa fa-times close" onClick={() => {
                Utils.updateSearchParams("sprize", null, setSearchParams);
                setDataComponent?.(undefined);
            }} />
        </div>
        <div className="mt-participant-data-details">
            {(tournamentPrize.type === "TOP_WINNER")
                && (<span className="text-shadowed" style={{ position: "absolute", top: 5, fontSize: 18, color: "var(--invertedTextColor)", "--textShadowColor": "var(--textColor)" } as any}>
                    {Utils.numberWithNth(tournamentPrize.position)}</span>)}
            <img alt={tournamentPrize.external_id} src={tournamentPrize.logo_url} style={{ width: 120, height: 120, borderRadius: "unset" }} />
            {(tournamentPrize.type === "TROPHY") && (<span className="n" style={{ fontWeight: "unset", fontSize: 16 }}>{tournamentPrize.title}</span>)}
            <span className="noseur-pill noseur-secondary" style={{ marginTop: 5, padding: "0.2rem 0.6rem" }}>{labels.details.type_labels[tournamentPrize.prize_type.toLowerCase()]}</span>
        </div>
        {(!distributed) && (<>
            {(tournamentPrize.projected_winner && !tournamentPrize.manually_selected_winner) && (<div className="mt-participant-data-actions">
                <span style={{ opacity: 0.5 }}>{labels.details.projected_winner}</span>
                <div style={{ display: "flex", alignItems: "center", width: "max-content", marginTop: 10 }}>
                    <Snapper header={{
                        ["TROPHY"]: labels.details.trophy_prize_projected_winner_media_snap,
                        ["TOP_WINNER"]: labels.details.top_winner_projected_winner_media_snap,
                    }[tournamentPrize.type]} template={[{ p: "tour_name", v: tournamentEdition.name }, { p: "name", v: tournamentPrize.title }, { p: "position", v: Utils.numberWithNth(tournamentPrize.position) }]} />
                    <img alt={projectedWinnerName} src={projectedWinnerImage} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                    <span>{projectedWinnerName}</span>
                </div>
            </div>)}
            {(tournamentPrize.manually_selected_winner) && (<>
                <div className="mt-participant-data-actions">
                    <span style={{ opacity: 0.5 }}>{labels.details.manually_selected_winner}</span>
                    <div style={{ display: "flex", alignItems: "center", width: "max-content", marginTop: 10 }}>
                        <Snapper header={labels.details.prize_selected_winner_media_snap} template={[{ p: "tour_name", v: tournamentEdition.name }, { p: "name", v: tournamentPrize.title }, { p: "position", v: Utils.numberWithNth(tournamentPrize.position) }]} />
                        <img alt={tournamentPrize.manually_selected_winner.external_id} src={Utils.getResourceImage(tournamentPrize.manually_selected_winner?.resource)} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                        <span>{Utils.getResourceName(tournamentPrize.manually_selected_winner?.resource)}</span>
                    </div>
                </div>
                {(tournamentPrize.projected_winner) && (<div className="mt-participant-data-actions">
                    <span style={{ opacity: 0.5 }}>{labels.details.rightful_winner}</span>
                    <div style={{ display: "flex", alignItems: "center", width: "max-content", marginTop: 10 }}>
                        <Snapper header={labels.details.prize_rightful_winner_media_snap} template={[{ p: "tour_name", v: tournamentEdition.name }, { p: "name", v: tournamentPrize.title }, { p: "position", v: Utils.numberWithNth(tournamentPrize.position) }]} />
                        <img alt={projectedWinnerName} src={projectedWinnerImage} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                        <span>{projectedWinnerName}</span>
                    </div>
                </div>)}
            </>)}
        </>)}
        {(distributed) && (<div className="mt-participant-data-actions">
            <span style={{ opacity: 0.5 }}>{labels.details.winner}</span>
            <div style={{ display: "flex", alignItems: "center", width: "max-content", marginTop: 10 }}>
                <Snapper header={labels.details.prize_winner_media_snap} template={[{ p: "tour_name", v: tournamentEdition.name }, { p: "name", v: tournamentPrize.title }, { p: "position", v: Utils.numberWithNth(tournamentPrize.position) }]} />
                <img alt={"Flaming Ninjaz"} src={"https://i.pinimg.com/736x/45/d1/83/45d183086872b1a137b6fcd6de2a7fef.jpg"} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                <span>{"Flaming Ninjaz"}</span>
            </div>
        </div>)}
        <div className="mt-participant-data-actions">
            <span className={Classname.build("link danger", { "noseur-disabled": distributed || currentEditionDistributed })} onClick={() => deletePrize(tournamentPrize)}>{labels.details.delete}</span>
            <span className={Classname.build("link", { "noseur-disabled": distributed || currentEditionDistributed })} onClick={() => AddOrEditTournamentPrize({
                tournamentEdition,
                prize: tournamentPrize,
                type: tournamentPrize.type,
                fetchInformations: props.refetch,
                prizeCountries: Object.keys(tournamentEdition.registration ?? {}).map((currencyCode) => {
                    return i18nManager.getCurrencyCountryCode(currencyCode);
                }),
            })}>{labels.details.edit_prize}</span>
            {(!tournamentPrize.manually_selected_winner) && (<span className={Classname.build("link", { "noseur-disabled": distributed || currentEditionDistributed })} onClick={() => manuallyOverrideWinner(tournamentPrize)}>{labels.details.manually_override_winner}</span>)}
            {(tournamentPrize.manually_selected_winner) && (<span className={Classname.build("link", { "noseur-disabled": distributed || currentEditionDistributed })} onClick={() => removeManuallySelectedWinner(tournamentPrize)}>{labels.details.remove_manually_override_winner}</span>)}
            {(!!tournamentPrize?.physical_prize?.how_to_claim && (currentEditionDistributed && !tournamentPrize?.physical_prize?.prize_claimed)) && (<span className={Classname.build("link", { "noseur-disabled": tournamentPrize?.physical_prize?.prize_claimed || distributed })} onClick={() => validatePhysicalPrizeClaim(tournamentPrize)}>{labels.details.validate_physical_prize}</span>)}
        </div>
        {(BoolHelper.equalsAny(tournamentPrize.prize_type, [TournamentPrizeType.MONETARY, TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE])) && (<div className="mt-participant-data-actions">
            <span>{labels.details.monetary_prizes}</span>
            {Object.keys(tournamentEdition.registration ?? {}).map((currency) => {
                const countryPrizePool = tournamentPrize?.currency_prizes?.[currency];
                if (!countryPrizePool) return null;
                const countryName = i18nManager.getCountryNameByCurrencyCode(currency);

                return (<div style={{ marginTop: 10 }} key={currency}>
                    <span style={{ fontStyle: "italic", marginRight: 5 }}>{countryName}:</span>
                    <span key={currency} style={{ fontWeight: "bold" }}>{StringHelper.toPresentableMoneyValue(countryPrizePool)} {currency}</span>
                </div>);
            })}
        </div>)}
        {(BoolHelper.equalsAny(tournamentPrize.prize_type, [TournamentPrizeType.PHYSICAL_PRIZE, TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE])) && (<div className="mt-participant-data-actions">
            <span>{labels.details.physical_prizes}</span>
            <div style={{ display: "flex", flexDirection: "column", marginTop: 15, borderRadius: 5, border: "2px solid var(--underlineColor)", position: "relative", height: 250, justifyContent: "flex-end" }}>
                <img alt={tournamentPrize.physical_prize?.name} src={tournamentPrize.physical_prize?.image_url} style={{ borderRadius: 5, height: "100%", width: "100%", position: "absolute" }} />
                <div style={{ display: "flex", flexDirection: "column", padding: "5px 0px", zIndex: 1, width: "100%", backgroundColor: "#ffffff8a" }}>
                    <span style={{ padding: "0px 5px" }}>{tournamentPrize.physical_prize?.name}</span>
                    <span style={{ padding: "0px 5px", fontSize: 13 }}><span style={{ opacity: 0.6 }}>{labels.details.how_to_claim}: </span>{tournamentPrize.physical_prize?.how_to_claim}</span>
                    <a style={{ padding: "0px 5px", width: "fit-content" }} href={tournamentPrize.physical_prize?.detail_link} target="_blank">{tournamentPrize.physical_prize?.detail_link}</a>
                </div>
            </div>
        </div>)}
    </div>);

    function deletePrize(tournamentPrize: ITournamentEditionPrize) {
        let tournamentName = "";
        const dialogLabels = labels.dialog.delete;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            confirmLabel: dialogLabels.delete,
            desc: dialogLabels.desc.replaceAll("${name}", (tournamentPrize.type === "TROPHY" ? tournamentPrize.title : `${Utils.numberWithNth(tournamentPrize.position)} ${dialogLabels.top_winner}`)),
            content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                <a href={dialogLabels.see_how.location} target="_blank">{dialogLabels.see_how.label}</a>
                <span style={{ marginTop: 15 }}>{i18nManager.Labels.tournaments.dialog.confirm_tournament.to_continue}</span>
                <FormControl isFieldset fill label={i18nManager.Labels.tournaments.dialog.confirm_tournament.tour_name} style={{ marginTop: 15 }}>
                    <TextInput scheme={Schemes.OMNEHOST} style={{ width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== tournamentEdition.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                consusService.deleteFile([tournamentPrize.logo_url, tournamentPrize.physical_prize?.image_url ?? ""]).catch(BaseService.reportError);
                tournamentService.deleteTournamentEditionPrizePool(tournamentEdition.external_id!, tournamentPrize.external_id).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                    dialog.hide(props.refetch);
                });
                return false;
            }
        });
        dialog.show();
    }

    function manuallyOverrideWinner(tournamentPrize: ITournamentEditionPrize) {
        let tournamentName = "";
        let selectedParticipantId = "";
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.manually_override_winner;

        function searchParticipants(search: string, onResult: (content: ITournamentEditionRegisteredParticipant[]) => void) {
            tournamentService.queryTournamentEditionRegisteredParticipants(tournamentEdition.external_id, {
                size: 20,
                "extra_data.participant_name": search,
                "tournament_edition_registered.status": "REGISTERED",
            }).then(({ content }: SanitizedResponse<any>) => {
                onResult(content);
            }).catch(BaseService.reportError);
        }

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            confirmLabel: dialogLabels.change,
            desc: dialogLabels.desc.replaceAll("${name}", (tournamentPrize.type === "TROPHY" ? tournamentPrize.title : `${Utils.numberWithNth(tournamentPrize.position)} ${dialogLabels.top_winner}`)),
            onLoading: async (alert: AlertInterface) => {
                searchParticipants("", (participants) => {
                    alert.doneLoading(() => {
                        alert.update({
                            __pass_through__: true,
                            content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                                <a href={dialogLabels.see_how.location} target="_blank">{dialogLabels.see_how.label}</a>
                                <a href={dialogLabels.alternative.location} target="_blank">{dialogLabels.alternative.label}</a>
                                <span style={{ marginTop: 15 }}>{dialogLabels.to_confirm}</span>
                                <FormControl isFieldset fill label={i18nManager.Labels.tournaments.dialog.confirm_tournament.tour_name} style={{ marginTop: 15 }}>
                                    <TextInput scheme={Schemes.OMNEHOST} style={{ width: "100%" }} onInputComplete={(v) => {
                                        dialogErrorRef.current!.innerText = " ";
                                        tournamentName = v;
                                    }} />
                                </FormControl>
                                <Dropdown fill editable scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: dialogLabels.new_winner, style: { marginTop: 10 } }}
                                    options={participants.map((v) => {
                                        (v as any).label = Utils.getResourceName(v.resource);
                                        (v as any).icon = Utils.getResourceImage(v.resource);
                                        return v;
                                    })} onSearch={(e, dropdownManageRef?: DropdownManageRef) => {
                                        searchParticipants(e.target.value, (participants) => {
                                            console.log("WE HERE....", participants);
                                            dropdownManageRef?.changeOptions(participants.map((v) => {
                                                (v as any).label = Utils.getResourceName(v.resource);
                                                (v as any).icon = Utils.getResourceImage(v.resource);
                                                return v;
                                            }));
                                        });
                                        return undefined;
                                    }}
                                    onSelectOption={(option) => {
                                        selectedParticipantId = option.external_id;
                                    }} />
                                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
                            </div>),
                        } as any);
                    });
                });
                return false;
            },
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== tournamentEdition.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.manuallyAttachTournamentEditionPrizePoolWinner(tournamentEdition.external_id, tournamentPrize.external_id, selectedParticipantId).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        props.refetch();
                        Siren.alert(dialogLabels.successfully_attached_winner);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function removeManuallySelectedWinner(tournamentPrize: ITournamentEditionPrize) {
        if (!tournamentPrize.manually_selected_winner) return;
        const dialogLabels = labels.dialog.remove_manual_winner;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            desc: dialogLabels.desc.replaceAll("${selected_winner}", Utils.getResourceName(tournamentPrize.manually_selected_winner.resource)),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                tournamentService.removeManuallyAttachTournamentEditionPrizePoolWinner(tournamentEdition.external_id, tournamentPrize.external_id).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        props.refetch();
                        Siren.alert(dialogLabels.successfully_removed_attached_winner);
                    });
                }).catch((err: any) => {
                    BaseService.reportError(err);
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function validatePhysicalPrizeClaim(tournamentPrize: ITournamentEditionPrize) {
        let tournamentName = "", claimCode = "";
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.validate_claim_physical_prize;
        const winnerName = Utils.getResourceName((tournamentPrize.manually_selected_winner ?? tournamentPrize.projected_winner)?.resource);

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.validate,
            desc: dialogLabels.desc.replaceAll("${name}", winnerName),
            content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                <a href={dialogLabels.claiming.location} target="_blank">{dialogLabels.claiming.label}</a>
                <span style={{ marginTop: 15 }}>{i18nManager.Labels.tournaments.dialog.confirm_tournament.to_continue}</span>
                <FormControl isFieldset fill label={i18nManager.Labels.tournaments.dialog.confirm_tournament.tour_name} style={{ marginTop: 15 }}>
                    <TextInput scheme={Schemes.OMNEHOST} style={{ width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentName = v;
                    }} />
                </FormControl>
                <FormControl isFieldset fill label={dialogLabels.claim_code} style={{ marginTop: 15 }}>
                    <TextInput scheme={Schemes.OMNEHOST} style={{ width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        claimCode = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== tournamentEdition.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.claimTournamentEditionPhysicalPrizePool(tournamentEdition.external_id, tournamentPrize.external_id, claimCode).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        props.refetch();
                        Siren.alert(dialogLabels.successfully_claimed);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default PrizePoolDetail;
